import type { Session } from '../types';

interface SessionsPanelProps {
  sessions: Session[];
  activeSessionId: string;
  isLoading: boolean;
  onSelectSession: (sessionId: string) => void;
  onNewChat: () => void;
  onClose: () => void;
}

/** Human-readable "time ago" for a session's updated_at ISO string. */
function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';

  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;

  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

export function SessionsPanel({
  sessions,
  activeSessionId,
  isLoading,
  onSelectSession,
  onNewChat,
  onClose,
}: SessionsPanelProps) {
  return (
    <>
      <style>{`
        @keyframes myiq-panel-in {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @keyframes myiq-shimmer {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.7; }
        }
        .myiq-sessions-panel {
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          width: 280px;
          z-index: 30;
          display: flex;
          flex-direction: column;
          background: var(--bg-card);
          border-left: 1px solid var(--border);
          box-shadow: -8px 0 24px rgba(0, 0, 0, 0.28);
          animation: myiq-panel-in 0.22s ease-out;
        }
        @media (max-width: 640px) {
          .myiq-sessions-panel {
            position: fixed;
            width: 100%;
          }
        }
        .myiq-session-item {
          width: 100%;
          text-align: left;
          background: transparent;
          border: none;
          border-left: 3px solid transparent;
          padding: 10px 16px;
          cursor: pointer;
          transition: background 0.15s;
        }
        .myiq-session-item:hover {
          background: var(--bg-elevated);
        }
        .myiq-session-item.active {
          border-left: 3px solid var(--accent);
          background: var(--accent-bg);
        }
        .myiq-newchat-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          margin: 12px 16px;
          padding: 8px 12px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          color: var(--accent);
          background: var(--accent-bg);
          border: 1px solid var(--border);
          cursor: pointer;
          transition: background 0.15s;
        }
        .myiq-newchat-btn:hover {
          background: var(--accent-hover);
          color: var(--btn-primary-text);
        }
      `}</style>

      <aside className="myiq-sessions-panel" aria-label="Recent chats">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontFamily: "'Outfit', sans-serif",
              fontSize: '15px',
              fontWeight: 700,
              color: 'var(--text-primary)',
            }}
          >
            Recent chats
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              display: 'flex',
              padding: '4px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-muted)',
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <button className="myiq-newchat-btn" onClick={onNewChat}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          New chat
        </button>

        <div style={{ flex: 1, overflowY: 'auto', paddingBottom: '8px' }}>
          {isLoading ? (
            [0, 1, 2, 3].map((i) => (
              <div
                key={i}
                data-testid="session-skeleton"
                style={{
                  height: '14px',
                  margin: '14px 16px',
                  borderRadius: '4px',
                  background: 'var(--bg-elevated)',
                  animation: 'myiq-shimmer 1.4s ease-in-out infinite',
                }}
              />
            ))
          ) : sessions.length === 0 ? (
            <p
              style={{
                margin: '24px 16px',
                fontSize: '13px',
                color: 'var(--text-muted)',
                textAlign: 'center',
              }}
            >
              No previous chats
            </p>
          ) : (
            sessions.map((s) => (
              <button
                key={s.session_id}
                className={
                  s.session_id === activeSessionId
                    ? 'myiq-session-item active'
                    : 'myiq-session-item'
                }
                onClick={() => onSelectSession(s.session_id)}
              >
                <div
                  style={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: 'var(--text-primary)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {s.title || 'Untitled chat'}
                </div>
                <div
                  style={{
                    marginTop: '3px',
                    fontSize: '11px',
                    color: 'var(--text-muted)',
                  }}
                >
                  {formatRelativeTime(s.updated_at)}
                </div>
              </button>
            ))
          )}
        </div>
      </aside>
    </>
  );
}
